import { mediaQueries, palette } from '@centivo/react-components'
import styled, { css } from 'styled-components'

import { useSliceTemplate } from '../../../context/global'
import { Content, ContentSection } from '../../structure'

export const StyledPractitionerDetails = styled(Content)`
  background-color: ${() => {
    if (useSliceTemplate()) {
      return '#F7F4E6'
    }
    return palette.neutral['grey-f6f7fa']
  }};
  min-height: 100%;
  height: fit-content;

  ${ContentSection} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
  }

  h1 {
    font-size: 48px;
    color: ${palette.brand.darkGreen};
    font-weight: 500;
    display: block;
    align-items: center;
  }

  h2 {
    color: ${palette.brand.darkGreen};
    font-weight: 500;
    font-size: 1.875rem;
    margin-right: 1rem;
  }
  h3 + h2 {
    margin: 1.5rem 0;
  }

  h3 {
    font-weight: 400;
    color: ${palette.brand.gray};
    font-size: 24px;
    margin-top: 4px;
  }

  .specialty-section {
    font-weight: 400;
    color: ${palette.brand.gray};
    font-size: 24px;
    margin-top: 4px;
  }

  p {
    font-size: 0.875rem;
    font-weight: 400;
    color: ${palette.neutral['grey-icons']};
  }
  p.distance {
    font-size: 0.75rem;
    font-weight: 700;
    color: ${palette.neutral['primary-body']};
    text-align: right;
    margin: 0 2.5rem 0 0.5rem;
  }

  div.card-attribute {
    background-color: ${palette.brand.lightPurple};
    border-radius: 20px;
    padding: 16px 20px 16px 20px;

    h4 {
      font-size: 20px;
    }

    div.value {
      font-size: 14px;
      display: flex;
      flex-direction: row;
    }

    p {
      text-transform: capitalize;
    }
  }

  svg + * {
    margin-left: 0.5rem;
  }

  .back-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
    height: 45px;
    width: fit-content;

    @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
      display: none;
    }
  }

  div.title {
    color: ${palette.brand.darkGreen};
    display: flex;
    flex-direction: row;
    font-size: 48px;
    font-weight: 700;
    margin: 0;

    span.title-name {
      display: flex;
      align-items: center;
      font-weight: 500;

      > svg {
        margin-left: 21.8px;
      }

      @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
        display: contents;

        > svg {
          margin-left: -2px;
          margin-bottom: -8px;
        }
      }
    }

    @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
      font-size: 36px;
      display: inline-block;
    }
  }

  div.map {
    height: 25rem;
    margin-bottom: 2rem;
  }

  div.location > div {
    margin: 0;
    display: flex;
    flex-direction: row;

    &:first-of-type {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      div {
        display: flex;
        flex-direction: column;

        div {
          display: flex;
          flex-direction: row;

          p {
            font-weight: 700;
          }
        }

        span {
          margin-left: 2rem;
        }
      }
    }

    &:not(:nth-last-of-type(-n + 2)) {
      grid-column: 1 / span 2;
    }

    svg + p {
      font-size: 1rem;
      font-weight: 500;
      color: ${palette.neutral['primary-body']};
    }
  }
  div.website-link {
    > button {
      background: none;
      border: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0);
      cursor: pointer;
      gap: 10px;
      display: flex;
      align-items: center;
      color: ${palette.brand.newPurple};
      font-size: 18px;
      font-weight: 500;
      width: fit-content;
    }

    > button:hover {
      border-bottom: 1px solid;
    }

    > button:focus {
      border: 1px solid;
      padding: 0px 8px 0px 8px;
      border-radius: 6px;
    }

    font-size: 1rem;
    font-weight: 600;
  }
  div.spacer {
    width: 27.5rem;

    @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
      width: auto;
    }
  }
`

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;

  @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
    width: 100%;
  }
`

export const Row = styled.div<{
  isMobileView: boolean
  firstElm: boolean
  marginBottom?: string
}>`
  display: flex;
  flex-direction: ${props => (props.isMobileView ? 'column' : 'row')};
  margin-top: ${props =>
    props.firstElm && props.isMobileView
      ? '2.5rem'
      : props.firstElm
      ? '1.5rem'
      : '2rem'};
  align-items: ${props => (props.isMobileView ? 'normal' : 'center')};

  ${({ isMobileView, marginBottom }) =>
    isMobileView && !marginBottom
      ? css`
          margin-bottom: 0rem;
        `
      : !marginBottom
      ? css`
          margin-bottom: 0.75rem;
        `
      : css`
          margin-bottom: ${marginBottom};
        `}
`

export const ContentGrid = styled.div`
  display: flex;
  grid-template-columns: auto;
  column-gap: 0.625rem;
  padding-top: 2.75rem;
  padding-bottom: 6rem;

  @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
    justify-content: center;
    padding: 2rem 0rem 2rem;
  }
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 1.25rem;
  row-gap: 1.25rem;

  @media ${mediaQueries.tablet}, ${mediaQueries.mobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`

export const Divider = styled.hr<{ isMobileView: boolean }>`
  border: 1px solid;
  color: ${palette.brand.lightPurple};
  background-color: ${palette.brand.lightPurple};
  flex-grow: 1;

  ${({ isMobileView }) =>
    isMobileView &&
    css`
      margin-top: 0.75rem !important;
      margin-bottom: 0.75rem !important;
    `}
`

export const RedirectModal = styled.div`
  padding-bottom: 8px;

  div {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }

  h1 {
    color: ${palette.brand.darkGreen};
    font-size: 1.75rem;
    font-weight: 600;
  }

  img {
    width: 42.21px;
    height: 32px;
  }

  p {
    padding: 16px 0 32px;
    font-weight: 400;
    font-size: 16px;
  }

  Button {
    width: 295px;
    height: 50px;
    font-weight: 500;
    font-size: 18px;
    background-color: ${palette.brand.orange};
    color: #44190a;
    border: #44190a;
    border-radius: 10px;
  }
`
